import { useState } from "react";
import { type HealthArea, type SchemaNames, SCHEMA_NAMES } from "instantdb/types";
import type { Resource } from "@/lib/types";
import { getResourceInfo } from "@/utilities/getResourceInfo";
import { useRoutines } from "@/hooks/routines/useRoutines";
import type { CommonIconType } from "@/icons/CommonIcon";
import { useResourceMutations } from "@/hooks/resources/useResourceMutations";
import { useResourceAtoms } from "@/hooks/resources/useResourceAtoms";
import { useGoals } from "@/hooks/goals/useGoals";
import { useAIHealthArea } from "@/hooks/healthAreas/useAIHealthArea";
import { useTranslation } from "@/hooks/useTranslation";

interface ResourceAction {
  label: string;
  onClick: () => void;
  icon: CommonIconType;
  separator?: boolean;
  disabled?: boolean;
}

/**
 * Generic hook for resource actions
 */
export const useResourceActions = (slug: SchemaNames, resource: Resource) => {
  const [openConfirmDeleteDialog, setConfirmDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>();

  const { labels } = getResourceInfo(slug);
  const { t } = useTranslation();

  const { handleEditResource } = useResourceAtoms({ slug });
  const { handleDelete } = useResourceMutations(slug);

  const { handleCreateGoal } = useGoals();
  const {
    handleSuggestGoal,
    handleSuggestRoutine,
    goalSuggestionLoading,
    routineSuggestionLoading,
  } = useAIHealthArea(resource as HealthArea);

  const { handleCreateRoutine } = useRoutines();

  const onDeleteResource = () => {
    setDeleteId(resource.id);
    setConfirmDeleteDialogOpen(true);
  };

  const onConfirmDelete = () => {
    if (deleteId) {
      handleDelete(deleteId, resource.title);
    }
  };

  // Edit & Delete
  const baseActions: ResourceAction[] = [
    {
      label: t("resource_actions.edit", { resource: labels.singular }),
      onClick: () => handleEditResource(resource),
      icon: "edit",
      separator: false,
    },
    {
      label: t("resource_actions.delete", { resource: labels.singular }),
      onClick: () => onDeleteResource(),
      icon: "delete",
      separator: false,
    },
  ];

  const customActions: Record<string, ResourceAction[]> = {
    // Health Areas
    [SCHEMA_NAMES.healthAreas]: [
      {
        label: t("resource_actions.add_routine"),
        onClick: () => handleCreateRoutine({ healthAreaId: resource.id }),
        icon: "routines",
        separator: false,
      },
      {
        label: t("resource_actions.add_goal"),
        onClick: () => handleCreateGoal({ healthAreaId: resource.id }),
        icon: "goals",
        separator: false,
      },
      {
        label: routineSuggestionLoading
          ? t("resource_actions.generating")
          : t("resource_actions.suggest_routine"),
        onClick: () => handleSuggestRoutine(resource as HealthArea),
        icon: "generate",
        disabled: routineSuggestionLoading,
        separator: true,
      },
      {
        label: goalSuggestionLoading
          ? t("resource_actions.generating")
          : t("resource_actions.suggest_goal"),
        onClick: () => handleSuggestGoal(resource as HealthArea),
        icon: "generate",
        disabled: goalSuggestionLoading,
        separator: false,
      },
    ],
    // Routines
    [SCHEMA_NAMES.routines]: [],
    // Goals
    [SCHEMA_NAMES.goals]: [],
  };

  const actions = [...(customActions[slug] ?? []), ...baseActions];

  return {
    openConfirmDeleteDialog,
    setConfirmDeleteDialogOpen,
    actions,
    handleDelete: onConfirmDelete,
  };
};
